import { HttpClient } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { HttpMethods } from '@cbre/http';
import { Sorting } from '@records/records-search/shared/search-filters.model';
import { PaginationSorting } from '@shared/components/complex-table/complex-table.model';
import { ColumnModel } from '@shared/models/columns/column-catalog.model';
import { DataObject } from '@shared/models/data-object.model';
import { IOption } from '@shared/models/icommons.model';
import { dateSanitizer } from '@shared/pipe/data-cell-formatter.pipe';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { OVERRIDE_SORTABLE_COLUMNS } from './utils/audit-history-filters.config';
import { EXTENDED_COLUMNS } from '../../audit-history/configs/audit-history-columns-override';
import {
  AuditHistoryRequest,
  AuditHistoryResponse,
  AuditHistorySearchResults,
  HistoricalStatsResults,
  HistoricalStatsPagination,
  HistoricalStatsResponse,
} from '../models/audit-history.model';
import { DataType } from '@miq/catalog';
import { LookupRefService } from './lookup-ref.service';

@Injectable()
export class AuditHistoryApiService extends HttpMethods {
  private centralAuditHistoryFilters: IOption[];
  constructor(
    http: HttpClient,
    private userInfoResolver: UserInfoResolver,
    @Optional() private lookupRef?: LookupRefService
  ) {
    super(http);
  }

  /**
   * Returns the current user ID stored in local storage
   * @returns number
   */
  getUserId(): number {
    const userInfo = this.userInfoResolver.resolve();
    return userInfo?.id != null ? userInfo.id : -1;
  }

  getAuditHistoryTemplate(): Observable<ColumnModel[]> {
    return this.httpGet('/audit/template').pipe(
      map(auditHistoryColumns => auditHistoryColumns.map(this.#toAuditHistoryColumnModel)),
      switchMap(columns => {
        const lookups$ = this.#fetchLookUpOptions(columns, { disableCache: true });
        return lookups$.pipe(
          map(lookups =>
            columns.map(column => ({
              ...column,
              values: lookups[column.field] ?? {},
            }))
          )
        );
      })
    ) as Observable<ColumnModel[]>;
  }

  #fetchLookUpOptions = (columns: ColumnModel[], cacheConfig: any) => {
    const { throwErr = false, disableCache = false, data = null } = cacheConfig || {};
    const lookups$ = columns
      .filter(column => column.lookupCategory != null)
      .reduce((acc, column) => {
        acc[column.field] = this.lookupRef.loadDataFor(column.lookupCategory, throwErr, disableCache, data);
        return acc;
      }, {});

    if (Object.keys(lookups$).length === 0) {
      return of({});
    }
    return forkJoin(lookups$);
  };

  #toAuditHistoryColumnModel = (column: ColumnModel & { sort: boolean }): any => ({
    ...column,
    header: column.value,
    sortable: OVERRIDE_SORTABLE_COLUMNS[column.field] ?? column.sort,
    displayFn: column.dataType === DataType.Date ? this.#dateDisplayFn(column) : undefined,
    ...(EXTENDED_COLUMNS[column.field] || {}),
  });

  #dateDisplayFn = (column: Pick<ColumnModel, 'field' | 'dataType'>) => (data: Record<string, any>) =>
    dateSanitizer(data[column.field], column.dataType !== 'timestamp with time zone', true);

  getAuditHistoryFilters(): Observable<AuditHistoryResponse> {
    return this.httpGet('/audit/filters') as Observable<AuditHistoryResponse>;
  }

  /**
   * Returns option count for filter options
   * @param request {FilterRequest}
   */
  getAggregations(request: AuditHistoryRequest) {
    return this.searchView('/audit/lookup', request) as Observable<any>;
  }

  getAuditHistoryList(
    filters: IOption[],
    paginationOpts?: PaginationSorting
  ): Observable<{ totalCount: number; results: AuditHistorySearchResults[] }> {
    const { limit, offset, sortField, sortFieldType, sortOrder } = paginationOpts;
    const pageOptions = { limit, offset };
    const sortOptions: Sorting = !!sortField
      ? {
          dataType: sortFieldType,
          field: sortField,
          order: sortOrder,
        }
      : null;
    return this.searchView('/audit/search', { filters, sort: sortOptions, ...pageOptions });
  }

  private searchView(url: string, request: AuditHistoryRequest) {
    const { entity, userId, filters } = request;
    let { offset } = request;
    if (offset != null) {
      /* MIQ-9452 ensure offset is positive integer value to prevent/correct
       * edge cases found in MIQ-9452 sending floating and/or negative offset values */
      offset = Math.floor(Math.abs(request.offset));
    }
    return this.httpPost(url, {
      entity,
      userId: userId != null ? userId : this.getUserId(),
      filters,
      offset,
      ...request,
    }) as Observable<any>;
  }

  public getHistoricalStatsAudits(
    filters: IOption[],
    paginationOpts: HistoricalStatsPagination = {}
  ): Observable<{ totalCount: number; results: HistoricalStatsResults[] }> {
    return this.httpPost('/stats/search', { userId: this.getUserId(), filters, ...paginationOpts });
  }

  getHistoricalStatsTemplate(): Observable<DataObject> {
    return this.httpGet('/stats/template');
  }

  public getHistoricalStatsFilters(): Observable<HistoricalStatsResponse> {
    return this.httpGet('/stats/filters');
  }

  public getHistoricalStatsLookUp(filters: IOption[]): Observable<{ totalCount: number; aggregation: DataObject[] }> {
    return this.httpPost('/stats/lookup', { userId: this.getUserId(), filters });
  }

  setFilters(filters: IOption[]) {
    this.centralAuditHistoryFilters = filters;
  }
  getFilters(): IOption[] {
    return this.centralAuditHistoryFilters;
  }
}
